import React from 'react';
import PropTypes from 'prop-types';
import {
  Field,
  FormSpy,
} from 'react-final-form';
import moment from 'moment';
import slugify from 'slugify';
import {
  TextField,
} from 'mui-rff';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Typography,
  TextField as MuiTextField,
  makeStyles,
  InputAdornment
} from '@material-ui/core';
import MapIcon from '@material-ui/icons/Map';
import WebsiteIcon from '@material-ui/icons/Language';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  placeTypeValues,
  statusValues,
} from 'src/utils/enums';
import PlaceType from 'src/components/PlaceType';
import TagsField from 'src/views/categories/components/TagsField';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  cardFooter: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    }
  },
  countryIcon: {
    fontSize: 15,
    '& > span': {
      marginRight: 10,
      fontSize: 18,
    },
  },
  headerButtons: {
    display: 'flex',
    gap: '8px',
    position: 'absolute;',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: 'white',
  },
  placeTypeIcon: {
    marginRight: theme.spacing(1),
  },
  streetViewField: {
    position: 'relative',
    '& input': {
      paddingRight: 48,
    },
  },
  streetViewButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  typeSelect: {
    '& img': {
      width: 17,
      height: 17,
      verticalAlign: 'middle',
    },
  },
}));

const PlaceDetails = ({
  data,
  setSlug,
}) => {
  const classes = useStyles();
  const isNarrowTable = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const openGoogleMaps = (latitude, longitude) => {
    if (latitude && longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`);
    }
  };

  return (
    <Card className={classes.root}>
      <CardHeader title={data ? `Edycja miejsca: ${data.title} [ID: #${data.id}]` : 'Dodaj nowe miejsce'} />
      <Divider />
      <CardContent>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={6}
            xs={12}
          >
            <TextField
              fullWidth
              inputProps={{
                onChange: (event) => {
                  setSlug(event.target.value);
                },
              }}
              label="Nazwa"
              name="title"
              required
              variant="outlined"
            />
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
          >
            <TextField
              className={classes.typeSelect}
              fullWidth
              label="Typ miejsca"
              name="type"
              required
              select
              variant="outlined"
            >
              <MenuItem
                key=""
                value=""
              />
              {placeTypeValues.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  <PlaceType
                    className={classes.placeTypeIcon}
                    type={option.value}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <TextField
              fullWidth
              label="Opis"
              multiline
              name="description"
              required
              rows={5}
              variant="outlined"
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <TagsField
              initialValue={data?.tags}
              name="tags"
              required
              label="Tagi"
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WebsiteIcon />
                  </InputAdornment>
                ),
              }}
              label="Strona internetowa"
              name="website"
              type="url"
              variant="outlined"
            />
          </Grid>
          <Grid
            className={classes.streetViewField}
            item
            xs={12}
          >
            <TextField
              fullWidth
              label="Google Maps Street View URL"
              name="streetViewUrl"
              variant="outlined"
            />
            <FormSpy
              subscription={{
                values: true,
              }}
            >
              {({ values }) => (
                <IconButton
                  disabled={!values.latitude && !values.longitude}
                  className={classes.streetViewButton}
                  onClick={() => openGoogleMaps(values.latitude, values.longitude)}
                >
                  <MapIcon />
                </IconButton>
              )}
            </FormSpy>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            md={6}
            xs={12}
          >
            <Field
              format={(value) => {
                if (value) {
                  return slugify(value, { lower: true, remove: /[*+~.()'"!:@]/g });
                }
                return '';
              }}
              name="slug"
            >
              {({ input, meta }) => (
                <MuiTextField
                  {...input}
                  fullWidth
                  error={!!meta.error}
                  helperText={!!meta.error && meta.error[0]}
                  label="Slug"
                  name="slug"
                  required
                  variant="outlined"
                />
              )}
            </Field>
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
          >
            <TextField
              fullWidth
              label="Status"
              name="status"
              required
              select
              variant="outlined"
            >
              {statusValues.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
              {data && (
                <MenuItem value="deleted">
                  Usunięty
                </MenuItem>
              )}
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      {data && (
        <CardContent className={classes.cardFooter}>
          <Grid
            container
            justify="space-between"
            spacing={1}
          >
            <Grid
              item
              sm={6}
              xs={12}
            >
              <Typography
                component="p"
                variant="caption"
              >
                Dodane:
                {' '}
                {moment(data.created).format('DD MMMM YYYY HH:mm')}
              </Typography>
            </Grid>
            <Grid
              item
              sm={6}
              xs={12}
            >
              <Typography
                align={isNarrowTable ? 'left' : 'right'}
                component="p"
                variant="caption"

              >
                Ostatnia edycja:
                {' '}
                {moment(data.updated).format('DD MMMM YYYY HH:mm')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      )}
    </Card>
  );
};

PlaceDetails.propTypes = {
  data: PropTypes.shape(),
  setSlug: PropTypes.func.isRequired,
};

PlaceDetails.defaultTypes = {
  data: null,
};

export default PlaceDetails;
